import { useEffect, useRef, useState } from "react";
import { clearCart } from "../lib/cart";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Header } from "../components/Header";
import { api } from "../lib/api";
import { eur } from "../lib/shield";
import { usePrivatePage } from "../lib/seo";
import { trackPurchase } from "../lib/analytics";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { WhatsAppShareButton } from "../components/WhatsAppShare";

const Shell = ({ children }) => { usePrivatePage("Order status"); return (
  <div className="min-h-screen">
    <Header onNew={() => { window.location.href = "/design"; }} />
    <main className="mx-auto max-w-xl px-4 py-16 text-center">{children}</main>
  </div>
); };

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const [design, setDesign] = useState(null);
  const svgRef = useRef(null);
  const draftId = state.data?.draft_id;
  useEffect(() => { if (draftId) api.get(`/drafts/${draftId}`).then((r) => setDesign(r.data.design)).catch(() => {}); }, [draftId]);

  useEffect(() => {
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          trackPurchase({ transaction_id: data.order_number, value: data.total / 100, items: [{ item_name: "Personalised shield", quantity: data.shield_count }] });
          clearCart();
          return setState({ phase: "paid", data });
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: "failed" });
      } catch (e) {
        if (e?.response?.status === 404) return setState({ phase: "failed" });
      }
      if (++tries >= 15) return setState({ phase: "timeout" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  if (state.phase === "checking") return <Shell><Loader2 className="mx-auto h-10 w-10 animate-spin" /><p className="mt-4 font-bold" data-testid="payment-checking">Confirming your payment…</p></Shell>;
  if (state.phase !== "paid") return (
    <Shell>
      <XCircle className="mx-auto h-12 w-12 text-red-600" />
      <h1 className="font-display mt-4 text-3xl font-black uppercase" data-testid="payment-failed">Payment not confirmed</h1>
      <p className="mt-2 text-slate-600">{state.phase === "timeout" ? "We couldn't confirm the payment yet. If you were charged, we'll be in touch by email." : "The payment didn't go through. You can try again."}</p>
      <Link to="/design" className="btn-blaze mt-6 inline-block px-5 py-2.5 text-sm">Back to designer</Link>
    </Shell>
  );
  const { data } = state;
  return (
    <Shell>
      <CheckCircle2 className="mx-auto h-14 w-14 text-lime-600" />
      <div className="eyebrow mt-4">Order confirmed</div>
      <h1 className="font-display text-4xl font-black uppercase" data-testid="payment-success">Thank you!</h1>
      <p className="mt-3 text-slate-600">Order <b className="font-mono-cc" data-testid="order-number">{data.order_number}</b> · {data.shield_count} shield{data.shield_count === 1 ? "" : "s"} · {eur(data.total)}</p>
      <p className="mt-1 text-sm text-slate-500" data-testid="payment-next-steps">A receipt is on its way to your email. {data.fulfilment === "collect" ? "We'll email you as soon as your shields are ready to collect from Business Print, Walkinstown." : data.fulfilment === "club" ? "Your shields will be delivered to your coach or club with the squad." : "We'll print your shields and deliver them to the address you gave."}</p>
      <div className="mt-8 border-2 border-[#25D366] bg-[#25D366]/10 p-4" data-testid="payment-share-box">
        <p className="text-sm font-bold">Proud of it? Share the shield with your team's WhatsApp group</p>
        <WhatsAppShareButton getSvg={() => svgRef.current} name={design?.player?.name?.trim()} className="mx-auto mt-3 px-5 py-2.5 text-sm" testId="payment-whatsapp-share-button" />
      </div>
      <Link to="/design" className="btn-blaze mt-6 inline-block px-5 py-2.5 text-sm" data-testid="payment-back-button">Design another shield</Link>
      {design && <div aria-hidden className="pointer-events-none fixed -left-[5000px] top-0 w-[400px]"><ShieldPreview ref={svgRef} design={design} testId="payment-share-shield" /></div>}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <h1 className="font-display text-3xl font-black uppercase" data-testid="payment-cancelled">Payment cancelled</h1>
      <p className="mt-2 text-slate-600">No charge was made. Your designs are still saved.</p>
      <Link to="/design" className="btn-blaze mt-6 inline-block px-5 py-2.5 text-sm">Back to designer</Link>
    </Shell>
  );
}
